import { Link } from "react-router-dom";
import { ArrowRight, CircleAlert, Edit3, MoreVertical, Radar, Trash2 } from "lucide-react";
import type { Partner } from "@/context/PartnerContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PartnerMiniPageStatus } from "@/components/admin/PartnerMiniPageStatus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const formatNumber = new Intl.NumberFormat("el-GR").format;
const shortDate = (value?: string | null): string => value
  ? new Intl.DateTimeFormat("el-GR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value))
  : "—";

interface PartnerDirectoryTableProps {
  partners: Partner[];
  onDelete: (partner: Partner) => void;
}

export function PartnerDirectoryTable({ partners, onDelete }: PartnerDirectoryTableProps) {
  return (
    <Card className="hidden overflow-hidden rounded-2xl border-slate-200 shadow-sm lg:block">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1120px] text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
            <tr>
              <th className="px-4 py-3.5">Συνεργάτης</th>
              <th className="px-4 py-3.5">Επικοινωνία</th>
              <th className="px-4 py-3.5">Routing</th>
              <th className="px-4 py-3.5">Mini page</th>
              <th className="px-4 py-3.5 text-right">Leads</th>
              <th className="px-4 py-3.5 text-right">30 ημέρες</th>
              <th className="px-4 py-3.5">Τελευταίο</th>
              <th className="px-4 py-3.5 text-right"><span className="sr-only">Ενέργειες</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {partners.map((partner) => {
              const canDelete = (partner.leadCount || 0) === 0;
              return (
                <tr key={partner.id} className="transition-colors hover:bg-slate-50/80">
                  <td className="max-w-72 px-4 py-3.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${partner.routingReady ? "bg-sky-950 text-white" : partner.active ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600"}`}>
                        {partner.name.trim().slice(0, 2).toLocaleUpperCase("el-GR") || "Σ"}
                      </div>
                      <div className="min-w-0">
                        <Link to={`/admin/partners/${partner.id}/dashboard`} className="block truncate font-semibold text-slate-950 hover:text-sky-700 hover:underline">{partner.name}</Link>
                        <p className="mt-0.5 truncate text-xs text-slate-500">{partner.location || "Χωρίς περιοχή"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="max-w-60 px-4 py-3.5">
                    <p className="truncate text-xs font-medium text-slate-700">{partner.phone || "Χωρίς τηλέφωνο"}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">{partner.email || "Χωρίς email"}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    {!partner.active ? (
                      <Badge variant="outline" className="border-slate-200 bg-slate-100 text-slate-600">Ανενεργός</Badge>
                    ) : partner.routingReady ? (
                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800"><Radar className="mr-1 h-3 w-3" /> Έτοιμος · {partner.radius || 0} km</Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800"><CircleAlert className="mr-1 h-3 w-3" /> Έλεγχος</Badge>
                    )}
                  </td>
                  <td className="max-w-56 px-4 py-3.5">
                    <Link
                      to={`/admin/partners/edit/${partner.id}`}
                      className="inline-flex min-h-11 max-w-full items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
                      aria-label={`Στοιχεία mini page για ${partner.name}`}
                    >
                      <PartnerMiniPageStatus partner={partner} compact />
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-slate-950">{formatNumber(partner.leadCount || 0)}</td>
                  <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-sky-700">{formatNumber(partner.leads30 || 0)}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-xs text-slate-600">{shortDate(partner.lastLead)}</td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="ghost" size="icon" className="h-10 w-10" aria-label={`Dashboard ${partner.name}`}>
                        <Link to={`/admin/partners/${partner.id}/dashboard`}><ArrowRight className="h-4 w-4" /></Link>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button type="button" variant="ghost" size="icon" className="h-10 w-10" aria-label={`Ενέργειες για ${partner.name}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem asChild><Link to={`/admin/partners/edit/${partner.id}`}><Edit3 className="mr-2 h-4 w-4" /> Επεξεργασία</Link></DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem disabled={!canDelete} onClick={() => canDelete && onDelete(partner)} className="text-rose-700 focus:bg-rose-50 focus:text-rose-800">
                            <Trash2 className="mr-2 h-4 w-4" />{canDelete ? "Οριστική διαγραφή" : "Δεν διαγράφεται — έχει leads"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
